import { Api } from "@/api/Api";
import reduxStore, { type RootState } from "@/reducer";
import { isProfileLocked } from "@/utils/profileLock";
import { buildTabSnapshot, hasUnsafeLayoutsForSnapshot } from "@/utils/tabSnapshot";
import Logger from "@mm/logging";
import type { TabSnapshot } from "@mm/metx-workbench.meteomatics.com";
import equal from "fast-deep-equal";
import { debounce } from "lodash";

const logger = Logger.fromFilename(__filename);

// Tabs already warned about, so the autosave tick doesn't log on every change.
const warnedUnsafeAutosaveTabIds = new Set<number>();

/**
 * A map to temporarily hold tab snapshots that are "queued to save, but not yet saved in backend"
 * The purpose of this is to allow keeping multiple tab snapshots until the save API calls emit,
 * in a potentially race condition such as tab switch.
 */
const tabSnapshotsByTabId: { [tabId: number]: RootState["tabGroup"]["present"] } = {};

let previousTabGroupState: RootState["tabGroup"]["present"];

export function tabAutoSaveListener() {
  const currentState = reduxStore.getState();
  const currentTabGroupState = currentState.tabGroup.present;

  // Only proceed if tabGroup state actually changed
  if (equal(previousTabGroupState, currentTabGroupState)) {
    return;
  }

  previousTabGroupState = currentTabGroupState;

  if (!currentTabGroupState.tabMeta.isLoading && currentTabGroupState.tabMeta.data) {
    const tabId = currentTabGroupState.tabMeta.data.id;
    tabSnapshotsByTabId[tabId] = currentTabGroupState;
  }
  const profile = currentState.profile?.profiles?.[currentTabGroupState.tabMeta.data?.id_profile];

  if (profile && !isProfileLocked(profile)) {
    saveTabStateDebounced(tabSnapshotsByTabId);
  }
}

const saveTabStateDebounced = debounce(saveTabSnapshot, 600);
function saveTabSnapshot(snapshotQueue: typeof tabSnapshotsByTabId) {
  const tabIds = Object.keys(snapshotQueue);
  for (const tabId of tabIds) {
    const tabState = snapshotQueue[+tabId];
    if (tabState) {
      const tabSnapshot = buildTabSnapshot(tabState);
      if (!tabSnapshot) {
        delete snapshotQueue[+tabId];
        continue;
      }
      if (hasUnsafeLayoutsForSnapshot(tabSnapshot)) {
        if (!warnedUnsafeAutosaveTabIds.has(+tabId)) {
          warnedUnsafeAutosaveTabIds.add(+tabId);
          logger.warn("Skipped tab autosave because the snapshot contains unsafe fallback layouts.", { tabId });
        }
        delete snapshotQueue[+tabId];
        continue;
      }

      Api.tab
        .v2SyncTabSnapshot({ tabSnapshot: tabSnapshot, tabId: +tabId })
        .then((tab) => {
          return tab;
        })
        .then(() => {
          // Remove the tab ID from queue so we don't trigger API request until the next action
          // to the tab is queued.
          delete snapshotQueue[+tabId];
          warnedUnsafeAutosaveTabIds.delete(+tabId);
        })
        .catch((e) => {
          // Note: In the future, we can raise an error message to the user if the tab is not saved.
          logger.error(e);
        });
    }
  }
}

export function flushAutosaveQueue() {
  saveTabStateDebounced.cancel();
  for (const id in tabSnapshotsByTabId) {
    delete tabSnapshotsByTabId[id];
  }
  warnedUnsafeAutosaveTabIds.clear();
}

// Direct helper for callers that already have a TabSnapshot structure.
export function saveTabSnapshotImmediate(tabId: number, tabSnapshot: unknown) {
  if (hasUnsafeLayoutsForSnapshot(tabSnapshot as TabSnapshot | null | undefined)) {
    const error = new Error("Skipped tab snapshot save because the snapshot contains unsafe fallback layouts.");
    logger.error(error, { tabId });
    return Promise.reject(error);
  }

  return Api.tab.v2SyncTabSnapshot({ tabSnapshot: tabSnapshot as any, tabId });
}
